import { createApi } from '@stilingue/st-auth-utils';
import Service from '@/services/Service';
import { error401Handler } from '@/utils/error';
import {
  parseResponse,
  parseTemplate,
  getUrl,
} from '@/services/charts-service/parsers';
import {
  TIME_FIELD,
  OPERATION,
  DATE_PORTION,
} from '@/services/charts-service/constants';
import { calculateVariation } from '@/utils/math';
import { displaceDateRange } from '@/utils/url';
import * as Sentry from '@sentry/browser';
import CryptoJS from 'crypto-js';

const gateway = process.env.VUE_APP_SERVICES_PROXY_URL || '';

export default class ChartsService extends Service {
  constructor(entity) {
    const api = createApi('/charts-service', gateway, {
      authNotFound: () => true,
      error401Handler,
    });

    super(api, { parseResponse });

    this.api.http.defaults.timeout = 600000;
    this.retriesLimit = 10;

    this.entity = entity;
  }

  async getByTemplate({ params = {}, template = {} } = {}, retries = 0) {
    const parsedTemplate = parseTemplate(this.entity, template);
    const requestParams = {
      ...parsedTemplate,
      url: getUrl(this.entity, {
        ...params,
        hash: CryptoJS.MD5(JSON.stringify(parsedTemplate)),
      }),
    };

    try {
      const response = await this.api.post(requestParams);

      return parseResponse(response);
    } catch (err) {
      if (this.retriesLimit && retries < this.retriesLimit) {
        return this.getByTemplate({ params, template }, retries + 1);
      }

      Sentry.captureException(err);
      throw new Error(err);
    }
  }

  getAggregatedData({ params = {}, ...options } = {}) {
    const {
      grouping = [],
      datePortion = DATE_PORTION.BY_DAY,
      fields: optFields = [],
      operators = OPERATION.COUNT,
    } = options;

    const fields = !Array.isArray(optFields)
      ? [optFields]
      : optFields;

    const label = options.label || fields[0] || 'counter';
    const template = {
      params,
      template: {
        datePortion,
        sorting: options.sorting,
        sortFields: options.sortFields,
        metricsMetadata: [
          {
            fields,
            label,
            groupingForTotal: [],
            operators: [operators],
          },
        ],
        grouping: [
          ...grouping,
        ],
      },
    };

    return this.getByTemplate(template);
  }

  getTimeSeries({ grouping = [], ...options }) {
    if (!TIME_FIELD[this.entity]) {
      throw new Error(`Invalid operation for ${this.entity}`);
    }

    if (!grouping.includes(TIME_FIELD[this.entity])) {
      grouping.push(TIME_FIELD[this.entity]);
    }

    return this.getAggregatedData({ grouping, ...options });
  }

  async getAggregatedMetric({ params = {}, reduceFunction, ...options }) {
    options.datePortion = DATE_PORTION.BY_YEAR;
    const { points } = reduceFunction
      ? await this.getAggregatedData({ params, ...options })
      : await this.getTimeSeries({ params, ...options });

    if (!points.length) {
      return 0;
    }
    return reduceFunction
      ? reduceFunction(points)
      : points[0].data.reduce((sum, value) => sum + value, 0);
  }

  async getMetricWithVariation({ params = {}, ...options }) {
    const { originalParams, displacedParams } = displaceDateRange(params);
    const [data, displacedData] = await Promise.all([
      this.getAggregatedMetric({ params: originalParams, ...options }),
      this.getAggregatedMetric({ params: displacedParams, ...options }),
    ]);

    return {
      collected_mentions: data,
      collected_previous_mentions: displacedData,
      collected_mentions_variation: calculateVariation(displacedData, data),
    };
  }

  async getDataWithDisplaceRange({ params = {}, ...options }) {
    const { originalParams, displacedParams } = displaceDateRange(params);
    const [data, displacedData] = await Promise.all([
      this.getAggregatedData({ params: originalParams, ...options }),
      this.getAggregatedData({ params: displacedParams, ...options }),
    ]);

    return {
      currentPeriod: data,
      displacedPeriod: displacedData,
    };
  }
}
